import { Eye, Briefcase, ShieldCheck, Target, LayoutGrid, Coffee } from "lucide-react";
import Eyebrow from "@/components/zeblan/Eyebrow";
import Reveal from "@/components/zeblan/Reveal";

const items = [
  { icon: Eye, title: 'The "Glass Box" approach', body: "AI shouldn't be a mystery. We build tools that are transparent, visible, and easy for your team to understand and control." },
  { icon: Briefcase, title: "Business first, tech second", body: "We use real-world business management experience to ensure the technology we build actually solves your specific operational needs." },
  { icon: ShieldCheck, title: "Total data protection", body: "We can run your AI models entirely locally, meaning your sensitive company data never leaves your building." },
  { icon: Target, title: "Guaranteed accuracy", body: "In a business setting, a tool that makes things up is useless. We design our systems to be factual and precise." },
  { icon: LayoutGrid, title: "Standardized, simple design", body: "A heavy focus on user experience means software your team can adopt without a steep learning curve." },
];

export default function WhyZeblan({ onOpenLead }) {
  return (
    <section id="why" className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow>Why Zeblan</Eyebrow>
          <h2 className="mt-6 text-navy font-extrabold tracking-tightish" style={{ fontSize: "clamp(30px,4vw,46px)" }}>
            What makes our <span className="font-display italic font-medium text-blue">approach</span> different
          </h2>
          <p className="mt-5 text-slate text-[17px] leading-relaxed">
            Technology is only as good as the people behind it. Here's how we build.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 60}>
              <article className="group h-full bg-white border border-mist rounded-2xl p-7 hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(10,19,40,0.25)] transition-all duration-300">
                <div className="h-11 w-11 rounded-lg bg-mist text-blue grid place-items-center group-hover:bg-blue group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-navy text-[18px] font-bold">{title}</h3>
                <p className="mt-3 text-slate text-[15px] leading-relaxed">{body}</p>
              </article>
            </Reveal>
          ))}

          {/* Standout card */}
          <Reveal delay={items.length * 60}>
            <article
              className="relative h-full overflow-hidden rounded-2xl p-7 text-white border border-white/10"
              style={{ background: "linear-gradient(140deg, #0A1328 0%, #122046 60%, #1D4FD0 130%)" }}
              data-testid="why-standout-card"
            >
              <div className="h-11 w-11 rounded-lg bg-teal/15 text-teal grid place-items-center">
                <Coffee className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-[18px] font-bold">A partner, not a vendor</h3>
              <p className="mt-3 text-mist/85 text-[15px] leading-relaxed">
                Whether you have a massive project in mind or just want to explore what's possible, our door is always
                open. Let's have a coffee and figure it out.
              </p>
              <button
                onClick={() => onOpenLead("contact")}
                className="mt-6 text-teal text-sm font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all"
                data-testid="why-standout-cta"
              >
                Start a conversation →
              </button>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
